import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import Search from "@mui/icons-material/Search";
import Clear from "@mui/icons-material/Clear";
import UnfoldMore from "@mui/icons-material/UnfoldMore";
import UnfoldLess from "@mui/icons-material/UnfoldLess";
import { Virtuoso } from "react-virtuoso";
import { useChatbotSlots } from "../../hooks/useChatbot";
import ChatbotTreeRow from "./ChatbotTreeRow";
import {
  LARGE_TREE_THRESHOLD,
  buildChildrenByParent,
  buildCollapseAllOpenNodes,
  buildExpandAllOpenNodes,
  buildInitialOpenNodes,
  buildSlotSearchIndex,
  computeSearchState,
  flattenVisibleTreeRows,
} from "./chatbotTreeModel";
import { buildOpenNodesForSlot } from "../../utils/chatbotFlowPathNavigation";

const ROW_HEIGHT = 48;

export default function ChatbotTreeView({
  userId,
  onSelectSlot,
  selectedSlotId,
  onAddChild,
  revealSlotId,
  simulatorActiveSlotId,
}) {
  const { data: slots = [], isLoading } = useChatbotSlots(userId);
  const [openNodes, setOpenNodes] = useState({});
  const [searchInput, setSearchInput] = useState("");
  const deferredSearch = useDeferredValue(searchInput);
  const initUserRef = useRef(null);
  const slotsInitRef = useRef(false);
  const virtuosoRef = useRef(null);

  const isLargeTree = slots.length > LARGE_TREE_THRESHOLD;

  const childrenByParent = useMemo(() => buildChildrenByParent(slots), [slots]);
  const roots = useMemo(() => childrenByParent.get("0") || [], [childrenByParent]);
  const searchIndex = useMemo(() => buildSlotSearchIndex(slots), [slots]);

  useEffect(() => {
    if (!userId) return;
    if (initUserRef.current !== userId) {
      initUserRef.current = userId;
      slotsInitRef.current = false;
      setSearchInput("");
      setOpenNodes({});
    }
  }, [userId]);

  useEffect(() => {
    if (!userId || !slots.length || slotsInitRef.current) return;
    slotsInitRef.current = true;
    setOpenNodes(buildInitialOpenNodes(slots, slots.length > LARGE_TREE_THRESHOLD));
  }, [userId, slots]);

  const searchState = useMemo(
    () => computeSearchState(slots, searchIndex, deferredSearch),
    [slots, searchIndex, deferredSearch]
  );

  const isSearching = Boolean(searchState.query);
  const isSearchPending = searchInput !== deferredSearch;

  useEffect(() => {
    if (!searchState.expandIds?.size) return;
    setOpenNodes((prev) => {
      const next = { ...prev };
      searchState.expandIds.forEach((id) => {
        next[id] = true;
      });
      return next;
    });
  }, [searchState.expandIds]);

  useEffect(() => {
    if (!revealSlotId || !slots.length) return;
    setOpenNodes((prev) => ({
      ...prev,
      ...buildOpenNodesForSlot(revealSlotId, slots),
    }));
  }, [revealSlotId, slots]);

  useEffect(() => {
    if (!simulatorActiveSlotId || !slots.length) return;
    setOpenNodes((prev) => ({
      ...prev,
      ...buildOpenNodesForSlot(simulatorActiveSlotId, slots),
    }));
  }, [simulatorActiveSlotId, slots]);

  const flatRows = useMemo(
    () =>
      flattenVisibleTreeRows({
        roots,
        childrenByParent,
        openNodes,
        visibleIds: searchState.visibleIds,
        isLargeTree: isLargeTree && !isSearching,
        forceExpand: isSearching,
      }),
    [roots, childrenByParent, openNodes, searchState.visibleIds, isLargeTree, isSearching]
  );

  useEffect(() => {
    if (!simulatorActiveSlotId || !flatRows.length) return;
    const index = flatRows.findIndex((row) => String(row.id) === String(simulatorActiveSlotId));
    if (index < 0) return;
    const frame = requestAnimationFrame(() => {
      virtuosoRef.current?.scrollToIndex?.({
        index,
        align: "center",
        behavior: "smooth",
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [simulatorActiveSlotId, flatRows]);

  const toggleNode = useCallback((id) => {
    setOpenNodes((prev) => {
      const isLarge = slots.length > LARGE_TREE_THRESHOLD;
      const currentlyOpen = isLarge ? prev[id] === true : prev[id] !== false;
      return { ...prev, [id]: !currentlyOpen };
    });
  }, [slots.length]);

  const handleExpandAll = useCallback(() => {
    if (slots.length > LARGE_TREE_THRESHOLD) {
      const ok = window.confirm(
        `Este fluxo tem ${slots.length.toLocaleString("pt-BR")} passos. Expandir tudo pode deixar a navegação mais lenta. Prefira usar a busca. Continuar?`
      );
      if (!ok) return;
    }
    setOpenNodes(buildExpandAllOpenNodes(slots));
  }, [slots]);

  const handleCollapseAll = useCallback(() => {
    setOpenNodes(buildCollapseAllOpenNodes(slots));
  }, [slots]);

  const renderRow = useCallback(
    (index) => {
      const row = flatRows[index];
      if (!row) return null;
      const id = row.id;
      return (
        <ChatbotTreeRow
          row={row}
          isSelected={String(selectedSlotId || "") === id}
          isSimulatorActive={String(simulatorActiveSlotId || "") === id}
          isDirectMatch={searchState.matchingIds.has(id)}
          searchQuery={searchState.query}
          onSelect={onSelectSlot}
          onToggle={toggleNode}
          onAddChild={onAddChild}
        />
      );
    },
    [
      flatRows,
      selectedSlotId,
      simulatorActiveSlotId,
      searchState.matchingIds,
      searchState.query,
      onSelectSlot,
      toggleNode,
      onAddChild,
    ]
  );

  if (!userId) return null;

  if (isLoading) {
    return (
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Carregando árvore…
        </Typography>
      </Box>
    );
  }

  if (!slots.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Nenhum passo encontrado. Inicialize o fluxo no modo Canvas.
        </Typography>
      </Box>
    );
  }

  const matchCount = searchState.matchingIds.size;
  const isSearchStale = isSearchPending && searchInput.trim().length > 0;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 320,
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flexShrink: 0, p: 1.5, pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
            Estrutura em árvore
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {slots.length.toLocaleString("pt-BR")} passos
            {flatRows.length !== slots.length ? ` · ${flatRows.length.toLocaleString("pt-BR")} visíveis` : ""}
          </Typography>
        </Box>

        {isLargeTree && !isSearching ? (
          <Alert severity="info" sx={{ py: 0, mb: 1, fontSize: "0.8rem" }}>
            Fluxo grande: ramos recolhidos por padrão. Use a <strong>busca</strong> ou expanda os nós necessários.
          </Alert>
        ) : null}

        <TextField
          size="small"
          fullWidth
          placeholder="Buscar passo (identificador, nome, mensagem…)"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isSearchStale ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : null}
                {searchInput ? (
                  <IconButton size="small" onClick={() => setSearchInput("")} aria-label="Limpar busca">
                    <Clear fontSize="small" />
                  </IconButton>
                ) : null}
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: "flex", gap: 0.5, mt: 1, flexWrap: "wrap" }}>
          <Button size="small" startIcon={<UnfoldMore fontSize="small" />} onClick={handleExpandAll} sx={{ textTransform: "none" }}>
            Expandir
          </Button>
          <Button size="small" startIcon={<UnfoldLess fontSize="small" />} onClick={handleCollapseAll} sx={{ textTransform: "none" }}>
            Recolher
          </Button>
        </Box>

        {isSearching ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
            {isSearchStale
              ? "Buscando…"
              : matchCount === 0
                ? "Nenhum passo encontrado."
                : `${matchCount.toLocaleString("pt-BR")} passo${matchCount === 1 ? "" : "s"} encontrado${matchCount === 1 ? "" : "s"}.`}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, px: 0.5, pb: 1 }}>
        {flatRows.length === 0 && isSearching && !isSearchStale ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 1.5 }}>
            Tente outro termo ou limpe a busca.
          </Typography>
        ) : (
          <Virtuoso
            ref={virtuosoRef}
            style={{ height: "100%" }}
            totalCount={flatRows.length}
            overscan={200}
            defaultItemHeight={ROW_HEIGHT}
            itemContent={renderRow}
          />
        )}
      </Box>
    </Box>
  );
}
